/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { forwardRef } from 'react';

/**
 * Button variant.
 * - primary: filled brand colour (default for main actions)
 * - secondary: outlined, neutral background
 * - danger: filled red, for destructive actions; always includes an icon (T2)
 * - ghost: transparent background, used in toolbars
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

/**
 * Button size. All sizes meet TrueRead T3 on md and lg (touch-target-min).
 * - sm: compact height for dense desktop toolbars only (not primary touch surfaces)
 * - md: default; meets minimum tap target via tokens
 * - lg: primary actions on touch surfaces
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonPropsBase = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>;

export type ButtonProps =
  | (ButtonPropsBase & {
      variant?: 'primary' | 'secondary' | 'ghost';
      size?: ButtonSize;
      icon?: ReactNode;
      children?: ReactNode;
    })
  | (ButtonPropsBase & {
      variant: 'danger';
      size?: ButtonSize;
      icon: ReactNode;
      children?: ReactNode;
    });

/**
 * TrueRead-compliant button.
 * - T1: contrast via design tokens
 * - T2: danger variant requires icon (colour plus shape)
 * - T3: minimum tap target on md and lg
 * - T4: body size via tokens, Sora via tokens
 * - T6: predictable, no auto state changes
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    children,
    className,
    type = 'button',
    ...rest
  } = props;

  const classes = ['ecs-button', `ecs-button--${variant}`, `ecs-button--${size}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} {...rest}>
      {icon !== undefined && icon !== null && (
        <span className="ecs-button__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </button>
  );
});
